import { PDFDocument, rgb, StandardFonts } from 'pdf-lib';
import { translations } from '../translations/index';
import { getHeaderLogoDataUrl, getFooterLogoDataUrl } from './logoToDataUrl';

// Helper function to load image from local path
async function loadLocalImageAsBytes(imageSrc: string): Promise<Uint8Array> {
  const response = await fetch(imageSrc);
  const arrayBuffer = await response.arrayBuffer();
  return new Uint8Array(arrayBuffer);
}

interface Range {
  category: string;
  min: number;
  max: number;
  color: string;
}

interface Fund {
  name: string;
  composition: string;
}

interface PDFData {
  profile: string;
  description: string;
  ranges: Range[];
  funds: Record<string, Fund[]>;
  score: number;
  language: 'en' | 'es' | 'pt';
  preloadedLogos?: Map<string, ArrayBuffer>; // Optional pre-loaded logos for optimization
  clientName?: string; // Client full name
  advisorName?: string; // Advisor full name
}

// Convert data URL to bytes
async function dataUrlToBytes(dataUrl: string): Promise<Uint8Array> {
  const base64 = dataUrl.split(',')[1];
  const binaryString = atob(base64);
  const bytes = new Uint8Array(binaryString.length);
  for (let i = 0; i < binaryString.length; i++) {
    bytes[i] = binaryString.charCodeAt(i);
  }
  return bytes;
}

// Generate icon based on score (Shield, Target, Sparkles, TrendingUp)
function generateProfileIconImage(score: number, color: string): Promise<string> {
  return new Promise((resolve) => {
    const canvas = document.createElement('canvas');
    canvas.width = 160;
    canvas.height = 160;
    const ctx = canvas.getContext('2d')!;

    ctx.clearRect(0, 0, canvas.width, canvas.height);

    const centerX = canvas.width / 2;
    const centerY = canvas.height / 2;

    // Draw circular background - light gray instead of black
    ctx.beginPath();
    ctx.arc(centerX, centerY, 70, 0, 2 * Math.PI);
    ctx.fillStyle = '#e5e7eb'; // Light gray
    ctx.fill();
    ctx.strokeStyle = '#d1d5db';
    ctx.lineWidth = 3;
    ctx.stroke();

    // Draw icon based on score
    ctx.strokeStyle = color;
    ctx.fillStyle = color;
    ctx.lineWidth = 5;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';

    if (score <= 20) {
      // Shield icon
      ctx.beginPath();
      ctx.moveTo(centerX, centerY - 30);
      ctx.lineTo(centerX - 22, centerY - 22);
      ctx.lineTo(centerX - 22, centerY);
      ctx.quadraticCurveTo(centerX - 22, centerY + 22, centerX, centerY + 33);
      ctx.quadraticCurveTo(centerX + 22, centerY + 22, centerX + 22, centerY);
      ctx.lineTo(centerX + 22, centerY - 22);
      ctx.closePath();
      ctx.stroke();
    } else if (score <= 40 || score <= 60) {
      // Target icon
      ctx.beginPath();
      ctx.arc(centerX, centerY, 28, 0, 2 * Math.PI);
      ctx.stroke();
      ctx.beginPath();
      ctx.arc(centerX, centerY, 16, 0, 2 * Math.PI);
      ctx.stroke();
      ctx.beginPath();
      ctx.arc(centerX, centerY, 4, 0, 2 * Math.PI);
      ctx.fill();
    } else {
      // TrendingUp icon
      ctx.beginPath();
      ctx.moveTo(centerX - 28, centerY + 15);
      ctx.lineTo(centerX - 8, centerY - 4);
      ctx.lineTo(centerX + 8, centerY + 8);
      ctx.lineTo(centerX + 28, centerY - 20);
      ctx.stroke();
      // Arrow head
      ctx.beginPath();
      ctx.moveTo(centerX + 28, centerY - 20);
      ctx.lineTo(centerX + 20, centerY - 16);
      ctx.moveTo(centerX + 28, centerY - 20);
      ctx.lineTo(centerX + 24, centerY - 12);
      ctx.stroke();
    }

    resolve(canvas.toDataURL('image/png'));
  });
}

// Generate bar chart as image data URL with normalized values
function generateBarChartImage(ranges: Range[]): Promise<string> {
  return new Promise((resolve) => {
    // High-DPI canvas for crisp text
    const scale = 2.5;
    const baseWidth = 480;
    const baseHeight = 340;
    
    const canvas = document.createElement('canvas');
    canvas.width = baseWidth * scale;
    canvas.height = baseHeight * scale;
    const ctx = canvas.getContext('2d')!;
    
    // Scale context for high-DPI
    ctx.scale(scale, scale);

    ctx.clearRect(0, 0, baseWidth, baseHeight);

    // Filter ranges with max > 0
    const activeRanges = ranges.filter(range => range.max > 0);
    
    // Calculate normalized values (same logic as UI)
    const midpoints = activeRanges.map(range => (range.min + range.max) / 2);
    const total = midpoints.reduce((sum, val) => sum + val, 0);
    const normalized = midpoints.map(val => (val / total) * 100);
    let rounded = normalized.map(val => Math.round(val));
    
    // BUSINESS RULE: Enforce 10% minimum for any non-zero allocation
    const belowMinimum = rounded.filter(val => val > 0 && val < 10);
    
    if (belowMinimum.length > 0) {
      const deficit = belowMinimum.reduce((sum, val) => sum + (10 - val), 0);
      rounded = rounded.map(val => val > 0 && val < 10 ? 10 : val);
      
      const aboveMinimum = rounded
        .map((val, idx) => ({ val, idx }))
        .filter(item => item.val >= 10)
        .sort((a, b) => b.val - a.val);
      
      let remaining = deficit;
      for (let i = 0; i < aboveMinimum.length && remaining > 0; i++) {
        const item = aboveMinimum[i];
        const reduction = Math.min(item.val - 10, Math.ceil(remaining / (aboveMinimum.length - i)));
        rounded[item.idx] -= reduction;
        remaining -= reduction;
      }
    }
    
    // Adjust to ensure sum is exactly 100, respecting 10% minimum
    const sum = rounded.reduce((a, b) => a + b, 0);
    if (sum !== 100 && rounded.length > 0) {
      const difference = 100 - sum;
      
      // Find the largest category that can absorb the adjustment without going below 10%
      const adjustable = rounded
        .map((val, idx) => ({ val, idx }))
        .filter(item => item.val > 10 || (item.val === 10 && difference > 0))
        .sort((a, b) => b.val - a.val);
      
      if (adjustable.length > 0) {
        rounded[adjustable[0].idx] += difference;
      }
    }
    
    const barHeight = 32;
    const barSpacing = 52;
    const leftMargin = 110;
    const rightMargin = 85;
    const barWidth = baseWidth - leftMargin - rightMargin;
    const startY = 35;

    activeRanges.forEach((range, index) => {
      const yPos = startY + index * barSpacing;
      const recommendedValue = rounded[index];

      // Draw category name (modern, medium weight)
      ctx.font = '600 13px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';
      ctx.fillStyle = '#1f2937'; // Darker gray for better contrast
      ctx.textAlign = 'right';
      ctx.textBaseline = 'middle';
      ctx.fillText(range.category, leftMargin - 12, yPos + barHeight / 2);

      // Draw background bar (light gray)
      ctx.fillStyle = '#f3f4f6';
      ctx.fillRect(leftMargin, yPos, barWidth, barHeight);

      // Draw subtle border
      ctx.strokeStyle = '#e5e7eb';
      ctx.lineWidth = 1;
      ctx.strokeRect(leftMargin, yPos, barWidth, barHeight);

      // Draw filled bar based on recommended value (colored, rounded corners)
      const fillWidth = (recommendedValue / 100) * barWidth;
      ctx.fillStyle = range.color;
      
      // Rounded rectangle for fill
      const radius = 3;
      ctx.beginPath();
      ctx.moveTo(leftMargin + radius, yPos);
      ctx.lineTo(leftMargin + fillWidth - radius, yPos);
      ctx.quadraticCurveTo(leftMargin + fillWidth, yPos, leftMargin + fillWidth, yPos + radius);
      ctx.lineTo(leftMargin + fillWidth, yPos + barHeight - radius);
      ctx.quadraticCurveTo(leftMargin + fillWidth, yPos + barHeight, leftMargin + fillWidth - radius, yPos + barHeight);
      ctx.lineTo(leftMargin + radius, yPos + barHeight);
      ctx.quadraticCurveTo(leftMargin, yPos + barHeight, leftMargin, yPos + barHeight - radius);
      ctx.lineTo(leftMargin, yPos + radius);
      ctx.quadraticCurveTo(leftMargin, yPos, leftMargin + radius, yPos);
      ctx.closePath();
      ctx.fill();

      // Draw recommended value (medium size, bold, colored)
      ctx.font = 'bold 20px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';
      ctx.fillStyle = range.color;
      ctx.textAlign = 'left';
      ctx.fillText(`${recommendedValue}%`, leftMargin + barWidth + 12, yPos + barHeight / 2);
    });

    resolve(canvas.toDataURL('image/png'));
  });
}

// Generate signature text as image using Dancing Script (loaded in browser via CSS)
async function generateSignatureImage(name: string, fontSize: number = 36): Promise<string> {
  // Ensure the font is loaded before drawing
  try {
    await document.fonts.load(`${fontSize}px 'Dancing Script'`);
  } catch {
    // Proceed anyway; canvas will fall back to a cursive font
  }

  const scale = 2; // Hi-DPI
  const padding = 8;
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d')!;

  // Measure text first (at 1x to get pixel size)
  ctx.font = `600 ${fontSize}px 'Dancing Script', cursive`;
  const measured = ctx.measureText(name);
  const textWidth = Math.ceil(measured.width);
  const textHeight = fontSize * 1.4; // generous line height

  canvas.width = (textWidth + padding * 2) * scale;
  canvas.height = (textHeight + padding * 2) * scale;
  ctx.scale(scale, scale);

  ctx.clearRect(0, 0, canvas.width, canvas.height);
  ctx.font = `600 ${fontSize}px 'Dancing Script', cursive`;
  ctx.fillStyle = '#1a1a1a';
  ctx.textBaseline = 'top';
  ctx.fillText(name, padding, padding * 0.5);

  return canvas.toDataURL('image/png');
}

export async function generateRiskProfilePDF(data: PDFData): Promise<Blob> {
  const pdfDoc = await PDFDocument.create();
  
  // Embed fonts
  const font = await pdfDoc.embedFont(StandardFonts.Helvetica);
  const fontBold = await pdfDoc.embedFont(StandardFonts.HelveticaBold);

  // Colors
  const headerBg = rgb(0.118, 0.122, 0.114); // #1e1f1d
  const white = rgb(1, 1, 1);
  const darkText = rgb(0.2, 0.2, 0.2); // #333333
  const grayText = rgb(0.467, 0.467, 0.467); // #777777
  const greenAccent = rgb(0.565, 0.851, 0.467); // #90d977

  // Determine profile color based on score
  let profileColor = rgb(0.376, 0.647, 0.980); // blue default
  if (data.score <= 20) {
    profileColor = rgb(0.376, 0.647, 0.980); // blue #60a5fa
  } else if (data.score <= 40) {
    profileColor = rgb(0.4, 0.8, 0.933); // cyan #67e8ee
  } else if (data.score <= 60) {
    profileColor = rgb(0.565, 0.851, 0.467); // green #90d977
  } else if (data.score <= 80) {
    profileColor = rgb(0.984, 0.647, 0.376); // orange #fb923c
  } else {
    profileColor = rgb(0.957, 0.376, 0.376); // red #f87171
  }

  const t = translations[data.language];

  // Format current date
  const now = new Date();
  const dayName = t.days[now.getDay()];
  const dayNum = now.getDate();
  const monthName = t.months[now.getMonth()];
  const year = now.getFullYear();
  
  // Format date according to language
  let dateString = '';
  if (data.language === 'en') {
    // English UK: Wednesday, 25 February 2026
    dateString = `${dayName}, ${dayNum} ${monthName} ${year}`;
  } else if (data.language === 'es') {
    // Spanish: Miércoles, 25 de febrero de 2026
    dateString = `${dayName}, ${dayNum} de ${monthName.toLowerCase()} de ${year}`;
  } else if (data.language === 'pt') {
    // Portuguese: Quarta-feira, 25 de fevereiro de 2026
    dateString = `${dayName}, ${dayNum} de ${monthName.toLowerCase()} de ${year}`;
  }

  // Function to draw header
  const drawHeader = async (page: any) => {
    const { width, height } = page.getSize();
    const headerHeight = 100;
    
    page.drawRectangle({
      x: 0,
      y: height - headerHeight,
      width: width,
      height: headerHeight,
      color: headerBg,
    });

    try {
      const headerLogoDataUrl = await getHeaderLogoDataUrl();
      const headerLogoBytes = await fetch(headerLogoDataUrl).then((res) => res.arrayBuffer());
      const headerLogo = await pdfDoc.embedPng(headerLogoBytes);
      const headerLogoDims = headerLogo.scale(0.2);
      
      page.drawImage(headerLogo, {
        x: (width - headerLogoDims.width) / 2,
        y: height - 70,
        width: headerLogoDims.width,
        height: headerLogoDims.height,
      });
    } catch (error) {
      console.error('Error loading header logo, using text fallback:', error);
      const logoText = 'DOMINION';
      const logoSize = 28;
      const logoTextWidth = fontBold.widthOfTextAtSize(logoText, logoSize);
      page.drawText(logoText, {
        x: (width - logoTextWidth) / 2,
        y: height - 65,
        size: logoSize,
        font: fontBold,
        color: white,
      });
    }
  };

  // Function to draw footer
  const drawFooter = async (page: any) => {
    const { width } = page.getSize();
    const footerBarHeight = 50;
    
    page.drawRectangle({
      x: 0,
      y: 0,
      width: width,
      height: footerBarHeight,
      color: headerBg,
    });

    // Draw footer isotipo on left
    try {
      const footerLogoDataUrl = await getFooterLogoDataUrl();
      const footerLogoBytes = await fetch(footerLogoDataUrl).then((res) => res.arrayBuffer());
      const footerLogo = await pdfDoc.embedPng(footerLogoBytes);
      const footerLogoDims = footerLogo.scale(0.15);
      
      const footerLogoY = (footerBarHeight - footerLogoDims.height) / 2;
      
      page.drawImage(footerLogo, {
        x: 50,
        y: footerLogoY,
        width: footerLogoDims.width,
        height: footerLogoDims.height,
      });
    } catch (error) {
      console.error('Error loading footer logo, using text fallback:', error);
      page.drawText('D', {
        x: 60,
        y: 18,
        size: 24,
        font: fontBold,
        color: white,
      });
    }

    // Website on right
    const websiteText = t.website;
    const websiteSize = 9;
    const websiteWidth = font.widthOfTextAtSize(websiteText, websiteSize);
    page.drawText(websiteText, {
      x: width - websiteWidth - 60,
      y: 18,
      size: websiteSize,
      font: font,
      color: white,
    });
  };

  // ===== PAGE 1 =====
  const page1 = pdfDoc.addPage([595, 842]); // A4 size
  const { width, height } = page1.getSize();

  // Draw header on page 1
  await drawHeader(page1);

  // ===== CONTENT AREA PAGE 1 =====
  let yPosition = height - 100 - 35; // Start after header

  // Main title - CENTERED
  const titleSize = 18;
  const titleWidth = fontBold.widthOfTextAtSize(t.title, titleSize);
  page1.drawText(t.title, {
    x: (width - titleWidth) / 2,
    y: yPosition,
    size: titleSize,
    font: fontBold,
    color: darkText,
  });

  yPosition -= 18;

  // Date below title - CENTERED, GRAY, SMALL
  const dateSize = 8;
  const dateWidth = font.widthOfTextAtSize(dateString, dateSize);
  page1.drawText(dateString, {
    x: (width - dateWidth) / 2,
    y: yPosition,
    size: dateSize,
    font: font,
    color: grayText,
  });

  yPosition -= 25;

  // Generate profile icon with circle
  const colorHex = data.score <= 20 ? '#60a5fa' : 
                   data.score <= 40 ? '#67e8ee' :
                   data.score <= 60 ? '#90d977' :
                   data.score <= 80 ? '#fb923c' : '#f87171';
  
  const iconDataUrl = await generateProfileIconImage(data.score, colorHex);
  const iconImageBytes = await fetch(iconDataUrl).then((res) => res.arrayBuffer());
  const iconImage = await pdfDoc.embedPng(iconImageBytes);

  const iconSize = 65;
  const iconX = (width - iconSize) / 2;
  
  page1.drawImage(iconImage, {
    x: iconX,
    y: yPosition - iconSize,
    width: iconSize,
    height: iconSize,
  });

  yPosition -= iconSize + 12;

  // "Your Risk Profile:" label - CENTERED
  const profileLabelSize = 12;
  const profileLabelWidth = fontBold.widthOfTextAtSize(t.profileTitle, profileLabelSize);
  page1.drawText(t.profileTitle, {
    x: (width - profileLabelWidth) / 2,
    y: yPosition,
    size: profileLabelSize,
    font: fontBold,
    color: darkText,
  });

  yPosition -= 22;

  // Profile name in color - CENTERED
  const profileSize = 16;
  const profileTextWidth = fontBold.widthOfTextAtSize(data.profile, profileSize);
  page1.drawText(data.profile, {
    x: (width - profileTextWidth) / 2,
    y: yPosition,
    size: profileSize,
    font: fontBold,
    color: profileColor,
  });

  yPosition -= 25;

  // Description text (word wrapped) - CENTERED
  const maxWidth = width - 120;
  const descriptionSize = 10;
  const words = data.description.split(' ');
  let line = '';
  const lines = [];

  for (const word of words) {
    const testLine = line + word + ' ';
    const testWidth = font.widthOfTextAtSize(testLine, descriptionSize);

    if (testWidth > maxWidth && line !== '') {
      lines.push(line.trim());
      line = word + ' ';
    } else {
      line = testLine;
    }
  }
  if (line.trim()) {
    lines.push(line.trim());
  }

  for (const textLine of lines) {
    page1.drawText(textLine, {
      x: (width - font.widthOfTextAtSize(textLine, descriptionSize)) / 2,
      y: yPosition,
      size: descriptionSize,
      font: font,
      color: darkText,
    });
    yPosition -= 14;
  }

  yPosition -= 20;

  // "Recommended Asset Allocation" title - CENTERED
  const allocationTitleWidth = fontBold.widthOfTextAtSize(t.allocationTitle, 12);
  page1.drawText(t.allocationTitle, {
    x: (width - allocationTitleWidth) / 2,
    y: yPosition,
    size: 12,
    font: fontBold,
    color: darkText,
  });

  yPosition -= 12; // Tighter spacing title to content

  // Generate and embed bar chart - CENTERED
  const chartDataUrl = await generateBarChartImage(data.ranges);
  const chartImageBytes = await fetch(chartDataUrl).then((res) => res.arrayBuffer());
  const chartImage = await pdfDoc.embedPng(chartImageBytes);

  const chartWidth = 320;
  const chartHeight = 246; // Maintain aspect ratio (480x370), slightly smaller
  const chartX = (width - chartWidth) / 2;
  
  page1.drawImage(chartImage, {
    x: chartX,
    y: yPosition - chartHeight,
    width: chartWidth,
    height: chartHeight,
  });

  yPosition -= chartHeight + 20; // Space between sections

  // "Recommended Ranges" title - CENTERED (moved from page 2)
  const activeRanges = data.ranges.filter(range => range.max > 0);
  const rangesTitleText = t.results?.recommendationsTitle || t.recommendationsTitle;
  const rangesTitleWidth = fontBold.widthOfTextAtSize(rangesTitleText, 12);
  page1.drawText(rangesTitleText, {
    x: (width - rangesTitleWidth) / 2,
    y: yPosition,
    size: 12,
    font: fontBold,
    color: darkText,
  });

  yPosition -= 18; // More space so it matches the visual spacing of the allocation title

  // Ranges list - CENTERED (moved from page 2)
  for (const range of activeRanges) {
    const rangeText = `${range.category}: ${range.min}% - ${range.max}%`;
    const rangeTextWidth = font.widthOfTextAtSize(rangeText, 10);
    page1.drawText(rangeText, {
      x: (width - rangeTextWidth) / 2,
      y: yPosition,
      size: 10,
      font: font,
      color: darkText,
    });
    yPosition -= 16;
  }

  // Footer disclaimer text on page 1
  const footerTextSize = 9;
  const footerText = t.footer;
  const footerTextWidth = font.widthOfTextAtSize(footerText, footerTextSize);
  
  page1.drawText(footerText, {
    x: (width - footerTextWidth) / 2,
    y: 70,
    size: footerTextSize,
    font: font,
    color: grayText,
  });

  // Draw footer on page 1
  await drawFooter(page1);

  // ===== PAGE 2 =====
  const page2 = pdfDoc.addPage([595, 842]); // A4 size
  const { height: height2 } = page2.getSize();

  // Draw header on page 2
  await drawHeader(page2);

  // ===== CONTENT AREA PAGE 2 =====
  let yPosition2 = height2 - 100 - 50; // Start after header with more space

  // "Suggested Funds" title - CENTERED (same size as other titles)
  const fundsTitleText = t.results?.fundsTitle || 'Suggested Funds';
  const fundsTitleWidth = fontBold.widthOfTextAtSize(fundsTitleText, 12);
  page2.drawText(fundsTitleText, {
    x: (width - fundsTitleWidth) / 2,
    y: yPosition2,
    size: 12,
    font: fontBold,
    color: darkText,
  });

  yPosition2 -= 35; // Much more space after title

  // Funds list by category - simple layout with green circle logos
  const logoSize = 24; // Más pequeño (era 32px, ahora 24px - reducción 25%)
  const logoMargin = 8; // Reducido (era 10px)
  const textStartX = (width / 2) - 170; // Left aligned with space for logo
  
  for (const range of activeRanges) {
    const categoryFunds = data.funds[range.category];
    if (!categoryFunds || categoryFunds.length === 0) continue;

    // Category name - bold, dark text, left aligned
    page2.drawText(range.category, {
      x: textStartX,
      y: yPosition2,
      size: 10, // Reducido de 11 a 10
      font: fontBold,
      color: darkText,
    });
    yPosition2 -= 18; // Reducido (era 22)

    // Funds with green circle logo + text
    for (const fund of categoryFunds) {
      // Check if logo is preloaded
      if (data.preloadedLogos && data.preloadedLogos.has(fund.name)) {
        try {
          // Use preloaded logo
          const logoBytes = data.preloadedLogos.get(fund.name)!;
          
          const logoImage = await pdfDoc.embedPng(logoBytes);
          const logoDims = logoImage.scale(logoSize / logoImage.width);
          
          const logoX = textStartX;
          const logoY = yPosition2 - logoDims.height;
          
          // Draw logo
          page2.drawImage(logoImage, {
            x: logoX,
            y: logoY,
            width: logoDims.width,
            height: logoDims.height,
          });
          
          // Draw fund name next to logo - VERTICALLY CENTERED with logo
          const textY = logoY + (logoDims.height / 2) - 3; // Center text vertically with logo
          page2.drawText(fund.name, {
            x: logoX + logoDims.width + logoMargin,
            y: textY,
            size: 9,
            font: font,
            color: darkText,
          });
          
          yPosition2 -= Math.max(logoDims.height, 20) + 4; // Space between funds
        } catch (error) {
          console.error(`Error embedding logo for ${fund.name}:`, error);
          // Fallback to bullet point if logo fails
          const bulletText = '•';
          const bulletX = textStartX;
          
          page2.drawText(bulletText, {
            x: bulletX,
            y: yPosition2 - 12,
            size: 12,
            font: fontBold,
            color: greenAccent,
          });
          
          page2.drawText(fund.name, {
            x: bulletX + 15,
            y: yPosition2 - 12,
            size: 9,
            font: font,
            color: darkText,
          });
          
          yPosition2 -= 16; // Reducido (era 20)
        }
      } else {
        // Fallback: no logo found, use bullet point
        const bulletText = '•';
        const bulletX = textStartX;
        
        page2.drawText(bulletText, {
          x: bulletX,
          y: yPosition2 - 12,
          size: 12,
          font: fontBold,
          color: greenAccent,
        });
        
        page2.drawText(fund.name, {
          x: bulletX + 15,
          y: yPosition2 - 12,
          size: 9,
          font: font,
          color: darkText,
        });
        
        yPosition2 -= 16; // Reducido (era 20)
      }
    }

    yPosition2 -= 14; // Aumentado para dar más espacio entre última fund y próxima categoría
  }

  // Footer disclaimer text on page 2
  page2.drawText(footerText, {
    x: (width - footerTextWidth) / 2,
    y: 70,
    size: footerTextSize,
    font: font,
    color: grayText,
  });

  // Draw footer on page 2
  await drawFooter(page2);

  // ═══════════════════════════════════════════════════════════════════
  // PAGE 3: SIGNATURE PAGE
  // ═══════════════════════════════════════════════════════════════════
  const page3 = pdfDoc.addPage([width, height]);

  // Draw header on page 3
  await drawHeader(page3);

  // Starting Y position - aligned with other pages content start
  let sigY = height - 100 - 50; // Same as page 2 starting position

  // Signature title - DARK TEXT (not green)
  const signatureTitle = t.signature.title;
  const signatureTitleWidth = fontBold.widthOfTextAtSize(signatureTitle, 14);
  
  page3.drawText(signatureTitle, {
    x: (width - signatureTitleWidth) / 2,
    y: sigY,
    size: 14,
    font: fontBold,
    color: darkText,
  });

  sigY -= 50; // Space after title

  const sigLeftMargin = 80;
  const sigLineLength = width - (sigLeftMargin * 2);
  const sigFieldSpacing = 50;

  // Calculate the width of the longest label to properly position the text/lines
  const labelSize = 11;
  const labelWidths = [
    fontBold.widthOfTextAtSize(t.signature.clientName, labelSize),
    fontBold.widthOfTextAtSize(t.signature.advisorName, labelSize),
    fontBold.widthOfTextAtSize(t.signature.signature, labelSize),
    fontBold.widthOfTextAtSize(t.signature.date, labelSize),
  ];
  const maxLabelWidth = Math.max(...labelWidths);
  const labelOffset = maxLabelWidth + 15; // Add padding after the longest label

  // ═══════════════════════════════════════════════════════════════════
  // CLIENT SECTION
  // ═══════════════════════════════════════════════════════════════════
  
  // CLIENT NAME (pre-printed if provided)
  page3.drawText(t.signature.clientName, {
    x: sigLeftMargin,
    y: sigY,
    size: 11,
    font: fontBold,
    color: darkText,
  });
  
  // Always draw the line
  page3.drawLine({
    start: { x: sigLeftMargin + labelOffset, y: sigY - 2 },
    end: { x: sigLeftMargin + sigLineLength, y: sigY - 2 },
    thickness: 0.5,
    color: rgb(0.6, 0.6, 0.6),
  });
  
  // Pre-print client name if provided (on top of the line)
  if (data.clientName) {
    page3.drawText(data.clientName, {
      x: sigLeftMargin + labelOffset,
      y: sigY + 2,
      size: 10,
      font: font,
      color: darkText,
    });
  }
  
  sigY -= sigFieldSpacing;

  // CLIENT SIGNATURE (empty line)
  page3.drawText(t.signature.signature, {
    x: sigLeftMargin,
    y: sigY,
    size: 11,
    font: fontBold,
    color: darkText,
  });
  
  page3.drawLine({
    start: { x: sigLeftMargin + labelOffset, y: sigY - 2 },
    end: { x: sigLeftMargin + sigLineLength, y: sigY - 2 },
    thickness: 0.5,
    color: rgb(0.6, 0.6, 0.6),
  });
  
  sigY -= sigFieldSpacing;

  // CLIENT DATE (empty line)
  page3.drawText(t.signature.date, {
    x: sigLeftMargin,
    y: sigY,
    size: 11,
    font: fontBold,
    color: darkText,
  });
  
  page3.drawLine({
    start: { x: sigLeftMargin + labelOffset, y: sigY - 2 },
    end: { x: sigLeftMargin + sigLineLength, y: sigY - 2 },
    thickness: 0.5,
    color: rgb(0.6, 0.6, 0.6),
  });
  
  sigY -= sigFieldSpacing + 30; // Extra space before advisor section

  // ═══════════════════════════════════════════════════════════════════
  // ADVISOR SECTION
  // ═══════════════════════════════════════════════════════════════════
  
  // ADVISOR NAME (pre-printed if provided)
  page3.drawText(t.signature.advisorName, {
    x: sigLeftMargin,
    y: sigY,
    size: 11,
    font: fontBold,
    color: darkText,
  });
  
  // Always draw the line first
  page3.drawLine({
    start: { x: sigLeftMargin + labelOffset, y: sigY - 2 },
    end: { x: sigLeftMargin + sigLineLength, y: sigY - 2 },
    thickness: 0.5,
    color: rgb(0.6, 0.6, 0.6),
  });
  
  // Pre-print advisor name if provided (on top of the line)
  if (data.advisorName) {
    page3.drawText(data.advisorName, {
      x: sigLeftMargin + labelOffset,
      y: sigY + 2,
      size: 10,
      font: font,
      color: darkText,
    });
  }
  
  sigY -= sigFieldSpacing;

  // ADVISOR SIGNATURE (with Dancing Script if name provided)
  page3.drawText(t.signature.signature, {
    x: sigLeftMargin,
    y: sigY,
    size: 11,
    font: fontBold,
    color: darkText,
  });
  
  // Always draw the line
  page3.drawLine({
    start: { x: sigLeftMargin + labelOffset, y: sigY - 2 },
    end: { x: sigLeftMargin + sigLineLength, y: sigY - 2 },
    thickness: 0.5,
    color: rgb(0.6, 0.6, 0.6),
  });
  
  // Pre-print advisor signature in Dancing Script if name provided
  if (data.advisorName) {
    const signatureImage = await generateSignatureImage(data.advisorName, 18);
    const signatureBytes = await fetch(signatureImage).then((res) => res.arrayBuffer());
    const signatureImageEmbed = await pdfDoc.embedPng(signatureBytes);
    const signatureDims = signatureImageEmbed.scale(0.5); // Adjust size as needed
    
    const signatureX = sigLeftMargin + labelOffset - 4;  // -4 aligns cursive text with the name above (compensates canvas left padding)
    const signatureY = sigY - 18; // Text baseline sits ON the Signature line (-18 compensates ~16pt empty bottom space in canvas)
    
    page3.drawImage(signatureImageEmbed, {
      x: signatureX,
      y: signatureY,
      width: signatureDims.width,
      height: signatureDims.height,
    });
  }
  
  sigY -= sigFieldSpacing;

  // ADVISOR DATE (pre-printed with current date)
  page3.drawText(t.signature.date, {
    x: sigLeftMargin,
    y: sigY,
    size: 11,
    font: fontBold,
    color: darkText,
  });
  
  // Always draw the line
  page3.drawLine({
    start: { x: sigLeftMargin + labelOffset, y: sigY - 2 },
    end: { x: sigLeftMargin + sigLineLength, y: sigY - 2 },
    thickness: 0.5,
    color: rgb(0.6, 0.6, 0.6),
  });
  
  // Pre-print current date (on top of the line)
  page3.drawText(dateString, {
    x: sigLeftMargin + labelOffset,
    y: sigY + 2,
    size: 10,
    font: font,
    color: darkText,
  });

  // Footer disclaimer text on page 3 (same as other pages)
  page3.drawText(footerText, {
    x: (width - footerTextWidth) / 2,
    y: 70,
    size: footerTextSize,
    font: font,
    color: grayText,
  });

  // Draw footer on page 3
  await drawFooter(page3);

  // Save PDF
  const pdfBytes = await pdfDoc.save();
  return new Blob([pdfBytes.buffer as ArrayBuffer], { type: 'application/pdf' });
}